import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { updateProfile } from "../../api/authApi";
import { useAuth } from "../../context/AuthContext";
import "./ProfilePage.css";

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase()).join("");
}

function formatDob(dob) {
  if (!dob) return "";
  const date = new Date(dob);
  if (Number.isNaN(date.getTime())) return dob;
  return date.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

function ProfilePage() {
  const { user, token, updateUser } = useAuth();

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [mobile, setMobile] = useState(user?.mobile || "");
  const [dob, setDob] = useState(user?.dob || "");
  const [location, setLocation] = useState(user?.location || "");
  const [profileDocument, setProfileDocument] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  // Not signed in - send them to log in first.
  if (!user) return <Navigate to="/login" replace />;

  function startEditing() {
    setName(user.name || "");
    setEmail(user.email || "");
    setMobile(user.mobile || "");
    setDob(user.dob || "");
    setLocation(user.location || "");
    setProfileDocument(null);
    setProfileImage(null);
    setError(null);
    setSuccess(null);
    setEditing(true);
  }

  function cancelEditing() {
    setEditing(false);
    setError(null);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      const updated = await updateProfile({ name, email, mobile, dob, location, profileDocument, profileImage }, token);
      updateUser(updated);
      setProfileDocument(null);
      setProfileImage(null);
      setSuccess("Profile updated.");
      setEditing(false);
    } catch (err) {
      setError(err.message || "We couldn't update your profile. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="tt-profile-wrap">
      <div className="tt-profile-card">
        <div className="tt-profile-edit">
          {!editing && (
            <button type="button" className="tt-profile-edit-btn" onClick={startEditing}>
              Edit Profile
            </button>
          )}
        </div>
        <div className="tt-profile-header">
          <div className="tt-profile-avatar">
            {user.profileImage ? (
              <img src={user.profileImage} alt={user.name} className="tt-profile-avatar-img" />
            ) : (
              <span className="tt-profile-avatar-fallback">{getInitials(user.name)}</span>
            )}
          </div>

          <div className="tt-profile-header-text">
            <h1 className="tt-profile-name">{user.name || "Your profile"}</h1>
            <p className="tt-profile-email">{user.email}</p>
          </div>
        </div>

        {success && !editing && <p className="tt-profile-success" role="status">{success}</p>}

        {!editing ? (
          <dl className="tt-profile-details">
            <div className="tt-profile-detail-row">
              <dt>Mobile</dt>
              <dd>{user.mobile || "—"}</dd>
            </div>
            <div className="tt-profile-detail-row">
              <dt>Date of birth</dt>
              <dd>{formatDob(user.dob) || "—"}</dd>
            </div>
            <div className="tt-profile-detail-row">
              <dt>Location</dt>
              <dd>{user.location || "—"}</dd>
            </div>
          </dl>
        ) : (
          <form className="tt-profile-form" onSubmit={handleSubmit}>
            {error && <p className="tt-profile-error" role="alert">{error}</p>}

            <label className="tt-profile-label" htmlFor="profile-name">Full name</label>
            <input
              id="profile-name"
              type="text"
              className="tt-profile-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
            />

            <label className="tt-profile-label" htmlFor="profile-email">Email</label>
            <input
              id="profile-email"
              type="email"
              className="tt-profile-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />

            <label className="tt-profile-label" htmlFor="profile-mobile">Mobile number</label>
            <input
              id="profile-mobile"
              type="tel"
              className="tt-profile-input"
              value={mobile}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                if (value.length <= 15) setMobile(value);
              }}
              inputMode="numeric"
              autoComplete="tel"
            />

            <label className="tt-profile-label" htmlFor="profile-dob">Date of birth</label>
            <input
              id="profile-dob"
              type="date"
              className="tt-profile-input"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              autoComplete="bday"
            />

            <label className="tt-profile-label" htmlFor="profile-location">Location</label>
            <input
              id="profile-location"
              type="text"
              className="tt-profile-input"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              autoComplete="address-level2"
              placeholder="City, State"
            />

            <label className="tt-profile-label" htmlFor="profile-image">
              Profile Image <span className="tt-profile-optional">(optional, JPG, JPEG, PNG)</span>
            </label>
            <input
              id="profile-image"
              type="file"
              className="tt-profile-input tt-profile-file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={(e) => setProfileImage(e.target.files?.[0] || null)}
            />

            <label className="tt-profile-label" htmlFor="profile-document">
              Verification document <span className="tt-profile-optional">(optional, PDF)</span>
            </label>
            <input
              id="profile-document"
              type="file"
              className="tt-profile-input tt-profile-file"
              accept="application/pdf"
              onChange={(e) => setProfileDocument(e.target.files?.[0] || null)}
            />

            <div className="tt-profile-form-actions">
              <button type="button" className="tt-profile-cancel-btn" onClick={cancelEditing} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="tt-profile-submit" disabled={loading}>
                {loading ? "Saving..." : "Save changes"}
              </button>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}

export default ProfilePage;
